import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { MyAvatar } from '../common/MyAvatar';
import './AboutView.scss';

const toDate = (value) => {
  if(!value) return null;
  if(value instanceof Date) return value;
  if(typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
};

// --- Location helpers --------------------------------------------------
// Pull country / city out of an event location. Prefers Google Places
// addressComponents (set on the event when available) and falls back to
// parsing the description string ("Eiffel Tower, Paris, France").

const getAddressComponents = (location) => {
  if(!location) return null;
  const components = location.addressComponents || location.address_components;
  return Array.isArray(components) ? components : null;
};

const getCountryFromLocation = (location) => {
  if(!location) return '';
  const components = getAddressComponents(location);
  if(components){
    const country = components.find(item => item.types?.includes('country'));
    if(country) return country.long_name || country.short_name || '';
  }
  // Fallback: last comma-separated segment of the description.
  const description = location.description;
  if(typeof description === 'string' && description.includes(',')){
    const parts = description.split(',').map(s => s.trim()).filter(Boolean);
    if(parts.length >= 2) return parts[parts.length - 1];
  }
  return '';
};

const getCityFromLocation = (location) => {
  if(!location) return '';
  if(location.city) return location.city;
  if(location.locality) return location.locality;
  const components = getAddressComponents(location);
  if(components){
    const cityTypes = [
      'locality',
      'postal_town',
      'sublocality',
      'administrative_area_level_3',
      'administrative_area_level_2',
      'administrative_area_level_1',
    ];
    for(const cityType of cityTypes){
      const component = components.find(item => item.types?.includes(cityType));
      if(component) return component.long_name;
    }
  }
  // Fallback: second-to-last segment of the description (skip if it
  // duplicates the country we'd extract).
  const description = location.description;
  if(typeof description === 'string' && description.includes(',')){
    const parts = description.split(',').map(s => s.trim()).filter(Boolean);
    if(parts.length >= 2){
      const candidate = parts[parts.length - 2];
      const country = parts[parts.length - 1];
      if(candidate && candidate !== country) return candidate;
    }
  }
  return '';
};

const getEventStartAt = (event) => {
  const startAt = toDate(event.startDate);
  if(!startAt) return null;
  const startTime = event.startTime ? toDate(event.startTime) : null;
  if(startTime){
    startAt.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
  }
  return startAt;
};

// Animates a number from 0 to `target` over `duration` ms after a `delay`.
// Uses ease-out cubic so the count slows visibly near the end.
const useCountUp = (target, duration = 1400, delay = 0) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if(!Number.isFinite(target) || target <= 0){
      setValue(target || 0);
      return undefined;
    }

    let raf;
    const timeoutId = setTimeout(() => {
      const startTime = performance.now();
      const step = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(Math.round(target * eased));
        if(progress < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);

    return () => {
      clearTimeout(timeoutId);
      if(raf) cancelAnimationFrame(raf);
    };
  }, [target, duration, delay]);

  return value;
};

const formatNumber = (n) => Number(n).toLocaleString('en-US');

const BigStatCard = ({label, value, accent, delay, icon}) => {
  const animated = useCountUp(value, 1400, delay);
  return (
    <div className="about-view__stat" style={{'--stat-delay': `${delay}ms`, '--stat-accent': accent}}>
      <div className="about-view__stat-icon" aria-hidden="true">{icon}</div>
      <div className="about-view__stat-value">{formatNumber(animated)}</div>
      <div className="about-view__stat-label">{label}</div>
    </div>
  );
};

const YearBars = ({yearEntries, maxCount}) => {
  return (
    <div className="about-view__year-chart" role="list">
      {yearEntries.map(([year, count], idx) => {
        const heightPercent = maxCount > 0 ? Math.max(2, (count / maxCount) * 100) : 0;
        return (
          <div
            className="about-view__year-bar"
            key={year}
            role="listitem"
            aria-label={`${year}: ${count} ${count === 1 ? 'event' : 'events'}`}
            style={{'--bar-delay': `${320 + idx * 55}ms`}}
          >
            <div className="about-view__year-bar-track">
              <div
                className="about-view__year-bar-fill"
                style={{height: `${heightPercent}%`}}
              />
              <div className="about-view__year-bar-count">{count}</div>
            </div>
            <div className="about-view__year-bar-label">{year}</div>
          </div>
        );
      })}
    </div>
  );
};

const TopCategoryRow = ({category, count, percent, delay}) => {
  return (
    <div
      className="about-view__rank-row"
      style={{'--row-delay': `${delay}ms`, '--bar-target-width': `${percent}%`}}
    >
      <div className="about-view__rank-icon" style={{backgroundColor: category.pinColor}}>
        <span aria-hidden="true">{category.defaultEmoji}</span>
      </div>
      <div className="about-view__rank-body">
        <div className="about-view__rank-label-row">
          <span className="about-view__rank-name">{category.name}</span>
          <span className="about-view__rank-count">{count}</span>
        </div>
        <div className="about-view__rank-bar">
          <div
            className="about-view__rank-bar-fill"
            style={{backgroundColor: category.pinColor}}
          />
        </div>
      </div>
    </div>
  );
};

const TopPlaceRow = ({name, count, percent, delay, emoji, accent}) => {
  return (
    <div
      className="about-view__rank-row"
      style={{'--row-delay': `${delay}ms`, '--bar-target-width': `${percent}%`}}
    >
      <div className="about-view__rank-icon" style={{backgroundColor: accent}}>
        <span aria-hidden="true">{emoji}</span>
      </div>
      <div className="about-view__rank-body">
        <div className="about-view__rank-label-row">
          <span className="about-view__rank-name">{name}</span>
          <span className="about-view__rank-count">{count}</span>
        </div>
        <div className="about-view__rank-bar">
          <div
            className="about-view__rank-bar-fill"
            style={{backgroundColor: accent}}
          />
        </div>
      </div>
    </div>
  );
};

const TopFriendRow = ({friend, count, percent, delay}) => {
  return (
    <div
      className="about-view__rank-row"
      style={{'--row-delay': `${delay}ms`, '--bar-target-width': `${percent}%`}}
    >
      <MyAvatar
        userAccount={friend}
        style={{height:36, width:36, fontSize:14, flexShrink:0}}
      />
      <div className="about-view__rank-body">
        <div className="about-view__rank-label-row">
          <span className="about-view__rank-name">{friend.name}</span>
          <span className="about-view__rank-count">{count}</span>
        </div>
        <div className="about-view__rank-bar">
          <div className="about-view__rank-bar-fill about-view__rank-bar-fill--friend"/>
        </div>
      </div>
    </div>
  );
};

const Highlight = ({label, value, delay}) => {
  return (
    <div className="about-view__highlight" style={{'--row-delay': `${delay}ms`}}>
      <div className="about-view__highlight-label">{label}</div>
      <div className="about-view__highlight-value">{value}</div>
    </div>
  );
};

const formatLongDate = (date) => date.toLocaleDateString('en-US', {
  month: 'long', day: 'numeric', year: 'numeric',
});

export function AboutView() {
  const viewingUserAccount = useSelector(state => state.viewingUserAccount);
  const viewingUserName = useSelector(state => state.viewingUserName);
  const rawFriends = useSelector(state => state.viewingUserFriends);
  const rawTrips = useSelector(state => state.viewingUserTrips);
  const rawCategories = useSelector(state => state.viewingUserCategories);
  const rawTags = useSelector(state => state.viewingUserTags);
  const rawEvents = useSelector(state => state.viewingUserEvents);

  // Stabilize the `|| {}` fallback so the hooks below don't re-run every
  // render when a Redux slice happens to be undefined.
  const friends = useMemo(() => rawFriends || {}, [rawFriends]);
  const trips = useMemo(() => rawTrips || {}, [rawTrips]);
  const categories = useMemo(() => rawCategories || {}, [rawCategories]);
  const tags = useMemo(() => rawTags || {}, [rawTags]);
  const events = useMemo(() => rawEvents || {}, [rawEvents]);

  const eventList = useMemo(() => Object.values(events), [events]);

  const totals = {
    events: eventList.length,
    trips: Object.keys(trips).length,
    friends: Object.keys(friends).length,
    categories: Object.keys(categories).length,
    tags: Object.keys(tags).length,
  };

  // Sorted dates for span / first / last calculations.
  const sortedDates = useMemo(() => {
    return eventList.map(getEventStartAt).filter(Boolean).sort((a, b) => a - b);
  }, [eventList]);
  const firstDate = sortedDates[0] || null;
  const lastDate = sortedDates[sortedDates.length - 1] || null;
  // Full calendar years between first and last event. We compare by year,
  // then back off one year if we haven't yet reached the same month/day.
  const yearsCovered = (() => {
    if(!firstDate || !lastDate) return 0;
    let years = lastDate.getFullYear() - firstDate.getFullYear();
    const monthDiff = lastDate.getMonth() - firstDate.getMonth();
    if(monthDiff < 0 || (monthDiff === 0 && lastDate.getDate() < firstDate.getDate())){
      years -= 1;
    }
    return Math.max(1, years);
  })();

  // Year breakdown for the bar chart, dense across the full range so empty
  // years still appear (helpful for visual rhythm).
  const yearEntries = useMemo(() => {
    if(!firstDate || !lastDate) return [];
    const counts = {};
    eventList.forEach(event => {
      const date = getEventStartAt(event);
      if(!date) return;
      const year = date.getFullYear();
      counts[year] = (counts[year] || 0) + 1;
    });
    const entries = [];
    for(let year = firstDate.getFullYear(); year <= lastDate.getFullYear(); year++){
      entries.push([year, counts[year] || 0]);
    }
    return entries;
  }, [eventList, firstDate, lastDate]);
  const maxYearCount = yearEntries.reduce((max, [, count]) => Math.max(max, count), 0);
  const mostActiveYear = yearEntries.reduce((best, entry) => {
    if(!best || entry[1] > best[1]) return entry;
    return best;
  }, null);

  // Top categories by event count.
  const topCategories = useMemo(() => {
    const counts = {};
    eventList.forEach(event => {
      counts[event.categoryId] = (counts[event.categoryId] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([id, count]) => ({category: categories[id], count}))
      .filter(item => item.category)
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [eventList, categories]);
  const maxCategoryCount = topCategories.reduce((max, c) => Math.max(max, c.count), 0);

  // Top friends by event tag count.
  const topFriends = useMemo(() => {
    const counts = {};
    eventList.forEach(event => {
      (event.friendIds || []).forEach(friendId => {
        counts[friendId] = (counts[friendId] || 0) + 1;
      });
    });
    return Object.entries(counts)
      .map(([id, count]) => ({friend: friends[id], count}))
      .filter(item => item.friend)
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [eventList, friends]);
  const maxFriendCount = topFriends.reduce((max, f) => Math.max(max, f.count), 0);

  // Top countries / top cities. Build these together so we share a single
  // pass over the events. We also collect unique-set sizes for the headline
  // big stats grid above.
  const {topCountries, topCities, uniqueCountryCount, uniqueCityCount} = useMemo(() => {
    const countryCounts = {};
    const cityCounts = {};
    eventList.forEach(event => {
      const country = getCountryFromLocation(event.location);
      if(country) countryCounts[country] = (countryCounts[country] || 0) + 1;
      const city = getCityFromLocation(event.location);
      if(city){
        // Disambiguate same-name cities across countries when we can.
        const key = country ? `${city}, ${country}` : city;
        cityCounts[key] = cityCounts[key] || {name: city, country, count: 0};
        cityCounts[key].count += 1;
      }
    });

    const sortedCountries = Object.entries(countryCounts)
      .map(([name, count]) => ({name, count}))
      .sort((a, b) => b.count - a.count);
    const sortedCities = Object.values(cityCounts)
      .sort((a, b) => b.count - a.count);

    return {
      topCountries: sortedCountries.slice(0, 6),
      topCities: sortedCities.slice(0, 6),
      uniqueCountryCount: sortedCountries.length,
      uniqueCityCount: sortedCities.length,
    };
  }, [eventList]);
  const maxCountryCount = topCountries.reduce((max, c) => Math.max(max, c.count), 0);
  const maxCityCount = topCities.reduce((max, c) => Math.max(max, c.count), 0);

  // Most-visited single place (by exact location.description match).
  const topPlace = useMemo(() => {
    const counts = {};
    eventList.forEach(event => {
      const description = event.location?.description;
      if(!description) return;
      counts[description] = (counts[description] || 0) + 1;
    });
    let best = null;
    Object.entries(counts).forEach(([description, count]) => {
      if(!best || count > best.count) best = {description, count};
    });
    return best;
  }, [eventList]);

  const bigStats = [
    {label: 'Events', value: totals.events, accent: '#6366f1', icon: '📅'},
    {label: 'Trips', value: totals.trips, accent: '#0ea5e9', icon: '🧳'},
    {label: 'Countries', value: uniqueCountryCount, accent: '#22c55e', icon: '🌍'},
    {label: 'Cities', value: uniqueCityCount, accent: '#14b8a6', icon: '🏙️'},
    {label: 'Friends', value: totals.friends, accent: '#f59e0b', icon: '👥'},
    {label: 'Categories', value: totals.categories, accent: '#10b981', icon: '🗂️'},
    {label: 'Tags', value: totals.tags, accent: '#ec4899', icon: '🏷️'},
    {label: 'Years', value: yearsCovered, accent: '#8b5cf6', icon: '⏳'},
  ];

  const highlights = [];
  if(firstDate) highlights.push({label: 'First memory', value: formatLongDate(firstDate)});
  if(lastDate && lastDate.getTime() !== firstDate?.getTime()){
    highlights.push({label: 'Most recent', value: formatLongDate(lastDate)});
  }
  if(mostActiveYear && mostActiveYear[1] > 0){
    highlights.push({label: 'Most active year', value: `${mostActiveYear[0]} · ${mostActiveYear[1]} events`});
  }
  if(topPlace){
    highlights.push({label: 'Most visited place', value: `${topPlace.description} · ${topPlace.count} events`});
  }
  if(topFriends[0]){
    highlights.push({label: 'Most-tagged friend', value: `${topFriends[0].friend.name} · ${topFriends[0].count} events`});
  }

  return (
    <main className="about-view">
      <section className="about-view__hero">
        <div className="about-view__hero-glow" aria-hidden="true"/>
        <div className="about-view__hero-content">
          <div className="about-view__hero-avatar">
            <MyAvatar
              userAccount={viewingUserAccount}
              name={viewingUserName}
              style={{width:96, height:96, fontSize:36}}
            />
          </div>
          <h1 className="about-view__hero-name">{viewingUserName || 'Second Memories'}</h1>
          {viewingUserAccount?.username &&
            <div className="about-view__hero-username">@{viewingUserAccount.username}</div>
          }
          <div className="about-view__hero-tagline">A life in numbers</div>
          <div className="about-view__hero-social" role="group" aria-label="Followers and following">
            <button
              type="button"
              className="about-view__hero-social-pill"
              onClick={() => alert('Followers not implemented yet.')}
            >
              <span className="about-view__hero-social-count">0</span>
              <span className="about-view__hero-social-label">Followers</span>
            </button>
            <button
              type="button"
              className="about-view__hero-social-pill"
              onClick={() => alert('Following not implemented yet.')}
            >
              <span className="about-view__hero-social-count">0</span>
              <span className="about-view__hero-social-label">Following</span>
            </button>
          </div>
        </div>
      </section>

      <section className="about-view__stats-grid" aria-label="Top-level stats">
        {bigStats.map((stat, idx) => (
          <BigStatCard
            key={stat.label}
            label={stat.label}
            value={stat.value}
            accent={stat.accent}
            icon={stat.icon}
            delay={120 + idx * 80}
          />
        ))}
      </section>

      {yearEntries.length > 0 && maxYearCount > 0 &&
        <section className="about-view__panel">
          <header className="about-view__panel-header">
            <h2>Events by Year</h2>
            {mostActiveYear && mostActiveYear[1] > 0 &&
              <div className="about-view__panel-subtitle">
                {mostActiveYear[0]} was your most active year with {mostActiveYear[1]} {mostActiveYear[1] === 1 ? 'event' : 'events'}
              </div>
            }
          </header>
          <YearBars yearEntries={yearEntries} maxCount={maxYearCount}/>
        </section>
      }

      {(topCategories.length > 0 || topFriends.length > 0) &&
        <section className="about-view__two-up">
          {topCategories.length > 0 &&
            <div className="about-view__panel">
              <header className="about-view__panel-header">
                <h2>Top Categories</h2>
              </header>
              <div className="about-view__rank-list">
                {topCategories.map((item, idx) => (
                  <TopCategoryRow
                    key={item.category.id}
                    category={item.category}
                    count={item.count}
                    percent={maxCategoryCount > 0 ? (item.count / maxCategoryCount) * 100 : 0}
                    delay={400 + idx * 80}
                  />
                ))}
              </div>
            </div>
          }

          {topFriends.length > 0 &&
            <div className="about-view__panel">
              <header className="about-view__panel-header">
                <h2>Most-Tagged Friends</h2>
              </header>
              <div className="about-view__rank-list">
                {topFriends.map((item, idx) => (
                  <TopFriendRow
                    key={item.friend.id}
                    friend={item.friend}
                    count={item.count}
                    percent={maxFriendCount > 0 ? (item.count / maxFriendCount) * 100 : 0}
                    delay={500 + idx * 80}
                  />
                ))}
              </div>
            </div>
          }
        </section>
      }

      {(topCountries.length > 0 || topCities.length > 0) &&
        <section className="about-view__two-up">
          {topCountries.length > 0 &&
            <div className="about-view__panel">
              <header className="about-view__panel-header">
                <h2>Top Countries</h2>
                <div className="about-view__panel-subtitle">
                  {uniqueCountryCount} {uniqueCountryCount === 1 ? 'country' : 'countries'} visited
                </div>
              </header>
              <div className="about-view__rank-list">
                {topCountries.map((item, idx) => (
                  <TopPlaceRow
                    key={item.name}
                    name={item.name}
                    count={item.count}
                    percent={maxCountryCount > 0 ? (item.count / maxCountryCount) * 100 : 0}
                    emoji="🌍"
                    accent="#22c55e"
                    delay={520 + idx * 80}
                  />
                ))}
              </div>
            </div>
          }

          {topCities.length > 0 &&
            <div className="about-view__panel">
              <header className="about-view__panel-header">
                <h2>Top Cities</h2>
                <div className="about-view__panel-subtitle">
                  {uniqueCityCount} {uniqueCityCount === 1 ? 'city' : 'cities'} explored
                </div>
              </header>
              <div className="about-view__rank-list">
                {topCities.map((item, idx) => (
                  <TopPlaceRow
                    key={`${item.name}|${item.country}`}
                    name={item.country ? `${item.name}, ${item.country}` : item.name}
                    count={item.count}
                    percent={maxCityCount > 0 ? (item.count / maxCityCount) * 100 : 0}
                    emoji="🏙️"
                    accent="#14b8a6"
                    delay={600 + idx * 80}
                  />
                ))}
              </div>
            </div>
          }
        </section>
      }

      {highlights.length > 0 &&
        <section className="about-view__panel">
          <header className="about-view__panel-header">
            <h2>Highlights</h2>
          </header>
          <div className="about-view__highlights">
            {highlights.map((item, idx) => (
              <Highlight
                key={item.label}
                label={item.label}
                value={item.value}
                delay={650 + idx * 90}
              />
            ))}
          </div>
        </section>
      }

      {totals.events === 0 &&
        <section className="about-view__empty">
          <div>No events yet — once you start adding memories, this page comes to life.</div>
        </section>
      }
    </main>
  );
}
